import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Plus, AlertCircle, MessageCircle, Clock, CheckCircle } from 'lucide-react'

import { apiClient } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { useOrganization } from '../contexts/OrganizationContext'
import {
  AmbulanceOrderCreate,
  AmbulanceOrderFormData,
  AmbulanceOrderListResponse,
  EMPTY_ORDER_FORM,
  validateFinnishSSN,
} from '../types/ambulance'

interface CostCenter {
  id: string
  code: string
  name: string
  show_on_ambulance_order?: boolean
}

interface UserInfo {
  display_name: string
  email: string
  phone: string | null
  role: string
  can_order_ambulance?: boolean
  can_review_ambulance?: boolean
}

export default function AmbulanceOrderPage() {
  const { t } = useTranslation('ambulance')
  const { t: tc } = useTranslation('common')
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { selectedOrgId } = useOrganization()

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [formData, setFormData] = useState<AmbulanceOrderFormData>(EMPTY_ORDER_FORM)
  const [ssnError, setSsnError] = useState<string | null>(null)

  // Cost center search state
  const [costCenterSearch, setCostCenterSearch] = useState('')
  const [showCostCenterDropdown, setShowCostCenterDropdown] = useState(false)
  const costCenterDropdownRef = useRef<HTMLDivElement>(null)

  // Fetch current user info
  const { data: userInfo } = useQuery<UserInfo>({
    queryKey: ['currentUser'],
    queryFn: () => apiClient.get('/auth/me'),
    staleTime: 5 * 60 * 1000,
  })

  // Fetch cost centers (only those marked for ambulance orders)
  const { data: costCentersData } = useQuery<{ data: CostCenter[] }>({
    queryKey: ['cost-centers', selectedOrgId, 'ambulance'],
    queryFn: () => {
      const params = new URLSearchParams()
      if (selectedOrgId) params.append('organization_id', selectedOrgId)
      params.append('ambulance_only', 'true')
      return apiClient.get(`/cost-centers?${params.toString()}`)
    },
  })

  // Filter cost centers by search
  const filteredCostCenters = costCentersData?.data.filter(cc =>
    cc.code.toLowerCase().includes(costCenterSearch.toLowerCase()) ||
    cc.name.toLowerCase().includes(costCenterSearch.toLowerCase())
  ) || []

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (costCenterDropdownRef.current && !costCenterDropdownRef.current.contains(event.target as Node)) {
        setShowCostCenterDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch my orders
  const { data: ordersData, isLoading: ordersLoading } = useQuery<AmbulanceOrderListResponse>({
    queryKey: ['ambulance-orders', 'my', selectedOrgId],
    queryFn: () => {
      const params = selectedOrgId ? `?organization_id=${selectedOrgId}` : ''
      return apiClient.get(`/ambulance-orders${params}`)
    },
  })

  // Pre-fill orderer info from user
  useEffect(() => {
    if (userInfo && !formData.orderer_name) {
      setFormData(prev => ({
        ...prev,
        orderer_name: userInfo.display_name || '',
        orderer_phone: userInfo.phone || '',
      }))
    }
  }, [userInfo])

  // Field-specific errors from API
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Extract error message from API response
  const getErrorMessage = (error: unknown): string => {
    if (!error || typeof error !== 'object') {
      return t('errors.saveFailed')
    }

    const err = error as { response?: { data?: { detail?: unknown } } }
    const detail = err.response?.data?.detail

    // API error code: {"detail": {"code": "INVALID_COST_CENTER"}}
    if (detail && typeof detail === 'object' && 'code' in detail) {
      const code = (detail as { code: string }).code
      const errorMap: Record<string, string> = {
        'INVALID_COST_CENTER': t('errors.invalidCostCenter'),
        'INVALID_ORGANIZATION': t('errors.saveFailed'),
        'FEATURE_NOT_ENABLED': t('errors.featureNotEnabled'),
        'INSUFFICIENT_PERMISSIONS': t('errors.noPermission'),
        'ORDER_NOT_FOUND': t('errors.orderNotFound'),
        'ACCESS_DENIED': t('errors.accessDenied'),
      }
      return errorMap[code] || `${t('errors.saveFailed')}: ${code}`
    }

    // Pydantic validation errors: {"detail": [{"loc": ["body", "field"], "msg": "..."}]}
    if (Array.isArray(detail)) {
      const errors: Record<string, string> = {}
      const fieldNameMap: Record<string, string> = {
        'orderer_name': t('orderForm.ordererName'),
        'orderer_phone': t('orderForm.ordererPhone'),
        'patient_name': t('orderForm.patientName'),
        'patient_ssn': t('orderForm.patientSsn'),
        'pickup_location': t('orderForm.pickupLocation'),
        'destination_location': t('orderForm.destinationLocation'),
        'pickup_date': t('orderForm.pickupDate'),
        'pickup_time': t('orderForm.pickupTime'),
        'pickup_department': t('orderForm.pickupDepartment'),
        'cost_center_id': t('orderForm.costCenter'),
        'order_type': t('orderForm.orderType'),
      }

      for (const validationError of detail as Array<{ loc: string[]; msg: string }>) {
        const fieldPath = validationError.loc
        const fieldName = fieldPath[fieldPath.length - 1]
        const localizedField = fieldNameMap[fieldName] || fieldName
        errors[fieldName] = `${localizedField}: ${validationError.msg}`
      }

      setFieldErrors(errors)

      if (Object.keys(errors).length > 0) {
        const firstError = Object.values(errors)[0]
        return firstError
      }
    }

    return t('errors.saveFailed')
  }

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: AmbulanceOrderCreate) => {
      const params = selectedOrgId ? `?organization_id=${selectedOrgId}` : ''
      return apiClient.post(`/ambulance-orders${params}`, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ambulance-orders'] })
      setIsFormOpen(false)
      setFormData(EMPTY_ORDER_FORM)
      setFieldErrors({})
      setCostCenterSearch('')
    },
    onError: (error: unknown) => {
      // Error message is extracted in render
      getErrorMessage(error)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validate SSN
    if (!validateFinnishSSN(formData.patient_ssn)) {
      setSsnError(t('validation.invalidSsn'))
      return
    }
    setSsnError(null)

    const submitData: AmbulanceOrderCreate = {
      order_type: formData.order_type,
      orderer_name: formData.orderer_name,
      orderer_phone: formData.orderer_phone,
      cost_center_id: formData.cost_center_id || undefined,
      pickup_department: formData.pickup_department,
      patient_name: formData.patient_name,
      // order_id_number is auto-generated by backend (starting from 1000)
      patient_ssn: formData.patient_ssn,
      pickup_location: formData.pickup_location,
      destination_location: formData.destination_location,
      pickup_date: formData.pickup_date,
      pickup_time: formData.pickup_time,
    }

    createMutation.mutate(submitData)
  }

  const handleInputChange = (field: keyof AmbulanceOrderFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (field === 'patient_ssn') {
      setSsnError(null)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fi-FI')
  }

  const formatTime = (timeStr: string) => {
    return timeStr.substring(0, 5)
  }

  // Check if user has ambulance permissions
  // Only system_admin has automatic access, organization_admin needs explicit permission
  const isSystemAdmin = userInfo?.role === 'system_admin'
  const canAccessAmbulance = isSystemAdmin || userInfo?.can_order_ambulance || userInfo?.can_review_ambulance

  if (userInfo && !canAccessAmbulance) {
    return (
      <div className="p-6">
        <div className="p-4 bg-red-100 text-red-700 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {t('errors.noPermission')}
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('navigation.ambulanceOrders')}
        </h1>
        <button
          onClick={() => setIsFormOpen(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('orderForm.title')}
        </button>
      </div>

      {/* Order Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">{t('orderForm.title')}</h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Order Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('orderForm.orderType')} *
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="order_type"
                        value="non_urgent"
                        checked={formData.order_type === 'non_urgent'}
                        onChange={(e) => handleInputChange('order_type', e.target.value)}
                        className="mr-2"
                      />
                      {t('orderForm.nonUrgent')}
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="order_type"
                        value="emergency"
                        checked={formData.order_type === 'emergency'}
                        onChange={(e) => handleInputChange('order_type', e.target.value)}
                        className="mr-2"
                      />
                      {t('orderForm.emergency')}
                    </label>
                  </div>
                </div>

                {/* Orderer Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('orderForm.ordererName')} *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.orderer_name}
                      onChange={(e) => handleInputChange('orderer_name', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('orderForm.ordererPhone')} *
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.orderer_phone}
                      onChange={(e) => handleInputChange('orderer_phone', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                {/* Cost Center & Department */}
                <div className="grid grid-cols-2 gap-4">
                  <div ref={costCenterDropdownRef} className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('orderForm.costCenter')}
                    </label>
                    <input
                      type="text"
                      value={costCenterSearch}
                      onChange={(e) => {
                        setCostCenterSearch(e.target.value)
                        setShowCostCenterDropdown(true)
                        // Clear selection if user starts typing
                        if (formData.cost_center_id) {
                          handleInputChange('cost_center_id', '')
                        }
                      }}
                      onFocus={() => setShowCostCenterDropdown(true)}
                      placeholder={t('orderForm.searchCostCenter')}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                    {showCostCenterDropdown && (
                      <div className="absolute z-10 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto mt-1">
                        {filteredCostCenters.length === 0 ? (
                          <div className="px-3 py-2 text-gray-500 text-sm">
                            {t('orderForm.noCostCenter')}
                          </div>
                        ) : (
                          filteredCostCenters.map((cc) => (
                            <div
                              key={cc.id}
                              onClick={() => {
                                handleInputChange('cost_center_id', cc.id)
                                setCostCenterSearch(`${cc.code} - ${cc.name}`)
                                setShowCostCenterDropdown(false)
                              }}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                            >
                              {cc.code} - {cc.name}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('orderForm.pickupDepartment')} *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.pickup_department}
                      onChange={(e) => handleInputChange('pickup_department', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                {/* Patient Info */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('orderForm.patientName')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.patient_name}
                    onChange={(e) => handleInputChange('patient_name', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                {/* order_id_number is auto-generated by backend (starting from 1000) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('orderForm.patientSsn')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.patient_ssn}
                    onChange={(e) => handleInputChange('patient_ssn', e.target.value.toUpperCase())}
                    placeholder="131052-308T"
                    className={`w-full border rounded-lg px-3 py-2 ${ssnError ? 'border-red-500' : ''}`}
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('orderForm.patientSsnHelp')}</p>
                  {ssnError && (
                    <p className="text-xs text-red-500 mt-1 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {ssnError}
                    </p>
                  )}
                </div>

                {/* Locations */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('orderForm.pickupLocation')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.pickup_location}
                    onChange={(e) => handleInputChange('pickup_location', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('orderForm.destinationLocation')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.destination_location}
                    onChange={(e) => handleInputChange('destination_location', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('orderForm.pickupDate')} *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.pickup_date}
                      onChange={(e) => handleInputChange('pickup_date', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('orderForm.pickupTime')} *
                    </label>
                    <input
                      type="time"
                      required
                      value={formData.pickup_time}
                      onChange={(e) => handleInputChange('pickup_time', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                {/* Error message */}
                {createMutation.isError && (
                  <div className="p-3 bg-red-100 text-red-700 rounded-lg">
                    <div className="flex items-center">
                      <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                      <span>{getErrorMessage(createMutation.error)}</span>
                    </div>
                    {Object.keys(fieldErrors).length > 1 && (
                      <ul className="mt-2 ml-7 text-sm list-disc">
                        {Object.entries(fieldErrors).slice(1).map(([field, msg]) => (
                          <li key={field}>{msg}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Buttons */}
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsFormOpen(false)
                      setFormData(EMPTY_ORDER_FORM)
                      setSsnError(null)
                      setFieldErrors({})
                      setCostCenterSearch('')
                      createMutation.reset()
                    }}
                    className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                  >
                    {t('orderForm.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createMutation.isPending ? tc('saving') : t('orderForm.submit')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* My Orders List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">{t('list.myOrders')}</h2>
        </div>

        {ordersLoading ? (
          <div className="p-8 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : ordersData?.data.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {t('list.noOrders')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('list.pickupDate')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('list.pickupTime')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('list.orderType')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('list.patient')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('list.costCenter')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('list.status')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">

                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {ordersData?.data.map((order) => (
                  <tr
                    key={order.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/ambulance-orders/${order.id}`)}
                  >
                    <td className="px-4 py-3 text-sm">{formatDate(order.pickup_date)}</td>
                    <td className="px-4 py-3 text-sm">{formatTime(order.pickup_time)}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        order.order_type === 'emergency'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {order.order_type === 'emergency' ? t('orderForm.emergency') : t('orderForm.nonUrgent')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{order.patient_name}</td>
                    <td className="px-4 py-3 text-sm">{order.cost_center_name || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`flex items-center gap-1 ${
                        order.status === 'reviewed' ? 'text-green-600' : 'text-yellow-600'
                      }`}>
                        {order.status === 'reviewed' ? (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            {t('list.reviewed')}
                          </>
                        ) : (
                          <>
                            <Clock className="w-4 h-4" />
                            {t('list.pending')}
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {order.has_comments && (
                        <span title={t('list.hasComments')}>
                          <MessageCircle className="w-4 h-4 text-blue-500" />
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
